const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

/**
 * MadBot micro:bit Link
 * Web Bluetooth UART bridge for MadBot AI Studio.
 *
 * Flow:
 * MadBot AI Studio → Web Bluetooth UART → micro:bit → RoboMorph / Bit-Z hardware
 */

const UART_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const UART_TX_UUID = '6e400003-b5a3-f393-e0a9-e50e24dcca9e'; // micro:bit → browser
const UART_RX_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e'; // browser → micro:bit

const sectionLabel = text => ({
    blockType: BlockType.LABEL,
    text
});

class Scratch3MadBotMicrobitLink {
    constructor (runtime) {
        this.runtime = runtime;

        this.device = null;
        this.server = null;
        this.service = null;
        this.txCharacteristic = null;
        this.rxCharacteristic = null;

        this.connected = false;
        this.deviceName = '';
        this.status = 'not connected';

        this.lastReceivedMessage = '';
        this.rxBuffer = '';
        this.messageCounter = 0;
        this.lastMessageHatCounter = 0;

        this.receivedValues = {};
        this.sensorValues = {
            temperature: 0,
            light: 0,
            compass: 0
        };

        this.acceleration = {
            x: 0,
            y: 0,
            z: 0
        };

        this.buttonStates = {
            A: 'not pressed',
            B: 'not pressed',
            'A+B': 'not pressed'
        };

        this.pinStates = {
            P0: false,
            P1: false,
            P2: false,
            LOGO: false
        };

        this.pinValues = {
            P0: 0,
            P1: 0,
            P2: 0,
            LOGO: 0
        };

        this.currentGesture = '';
        this.currentTilt = '';

        this.eventCounters = {};
        this.lastHatCounters = {};

        this._onDisconnectedBound = this.handleDisconnected.bind(this);
        this._onUartNotificationBound = this.handleUartNotification.bind(this);
    }

    getInfo () {
        return {
            id: 'madbotMicrobitLink',
            name: 'MadBot micro:bit Link',
            color1: '#00AEEF',
            color2: '#008FCC',
            color3: '#006F99',
            blocks: [
                sectionLabel('Connection'),
                {
                    opcode: 'connectMicrobit',
                    blockType: BlockType.COMMAND,
                    text: 'connect to micro:bit'
                },
                {
                    opcode: 'disconnectMicrobit',
                    blockType: BlockType.COMMAND,
                    text: 'disconnect micro:bit'
                },
                {
                    opcode: 'isMicrobitConnected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is micro:bit connected?'
                },
                {
                    opcode: 'microbitName',
                    blockType: BlockType.REPORTER,
                    text: 'micro:bit name'
                },
                {
                    opcode: 'connectionStatus',
                    blockType: BlockType.REPORTER,
                    text: 'connection status'
                },

                sectionLabel('Messages'),
                {
                    opcode: 'sendMessage',
                    blockType: BlockType.COMMAND,
                    text: 'send message [MESSAGE] to micro:bit',
                    arguments: {
                        MESSAGE: {
                            type: ArgumentType.STRING,
                            defaultValue: 'hello'
                        }
                    }
                },
                {
                    opcode: 'sendCommand',
                    blockType: BlockType.COMMAND,
                    text: 'send command [COMMAND]',
                    arguments: {
                        COMMAND: {
                            type: ArgumentType.STRING,
                            defaultValue: 'RM:STOP'
                        }
                    }
                },
                {
                    opcode: 'sendValue',
                    blockType: BlockType.COMMAND,
                    text: 'send value [KEY] = [VALUE]',
                    arguments: {
                        KEY: {
                            type: ArgumentType.STRING,
                            defaultValue: 'speed'
                        },
                        VALUE: {
                            type: ArgumentType.STRING,
                            defaultValue: '100'
                        }
                    }
                },
                {
                    opcode: 'whenMessageReceived',
                    blockType: BlockType.HAT,
                    text: 'when message received from micro:bit',
                    isEdgeActivated: true
                },
                {
                    opcode: 'receivedMessage',
                    blockType: BlockType.REPORTER,
                    text: 'received message'
                },
                {
                    opcode: 'receivedValue',
                    blockType: BlockType.REPORTER,
                    text: 'received value [KEY]',
                    arguments: {
                        KEY: {
                            type: ArgumentType.STRING,
                            defaultValue: 'light'
                        }
                    }
                },
                {
                    opcode: 'clearReceivedMessage',
                    blockType: BlockType.COMMAND,
                    text: 'clear received message'
                },

                sectionLabel('RoboMorph'),
                {
                    opcode: 'roboMorphMove',
                    blockType: BlockType.COMMAND,
                    text: 'RoboMorph move [DIRECTION] speed [SPEED]',
                    arguments: {
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'roboMoveMenu',
                            defaultValue: 'forward'
                        },
                        SPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 3
                        }
                    }
                },
                {
                    opcode: 'roboMorphStop',
                    blockType: BlockType.COMMAND,
                    text: 'RoboMorph stop'
                },
                {
                    opcode: 'roboMorphAction',
                    blockType: BlockType.COMMAND,
                    text: 'RoboMorph action [ACTION]',
                    arguments: {
                        ACTION: {
                            type: ArgumentType.STRING,
                            menu: 'roboActionMenu',
                            defaultValue: 'sit'
                        }
                    }
                },
                {
                    opcode: 'roboMorphServo',
                    blockType: BlockType.COMMAND,
                    text: 'set RoboMorph servo [SERVO] angle [ANGLE]',
                    arguments: {
                        SERVO: {
                            type: ArgumentType.STRING,
                            menu: 'roboServoMenu',
                            defaultValue: 'S1'
                        },
                        ANGLE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 90
                        }
                    }
                },
                {
                    opcode: 'roboMorphMode',
                    blockType: BlockType.COMMAND,
                    text: 'set RoboMorph robot mode [MODE]',
                    arguments: {
                        MODE: {
                            type: ArgumentType.STRING,
                            menu: 'roboModeMenu',
                            defaultValue: 'robot dog'
                        }
                    }
                },

                sectionLabel('Bit-Z'),
                {
                    opcode: 'bitZCarMove',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z car move [DIRECTION] speed [SPEED]',
                    arguments: {
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'bitZMoveMenu',
                            defaultValue: 'forward'
                        },
                        SPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 80
                        }
                    }
                },
                {
                    opcode: 'bitZCarTurn',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z car turn [DIRECTION] speed [SPEED]',
                    arguments: {
                        DIRECTION: {
                            type: ArgumentType.STRING,
                            menu: 'bitZTurnMenu',
                            defaultValue: 'left'
                        },
                        SPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 80
                        }
                    }
                },
                {
                    opcode: 'bitZCarStop',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z car stop'
                },
                {
                    opcode: 'bitZServo',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z set servo [PIN] angle [ANGLE]',
                    arguments: {
                        PIN: {
                            type: ArgumentType.STRING,
                            menu: 'bitZServoPinMenu',
                            defaultValue: 'P0'
                        },
                        ANGLE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 90
                        }
                    }
                },
                {
                    opcode: 'bitZShowText',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z show text [TEXT]',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Hello'
                        }
                    }
                },
                {
                    opcode: 'bitZSetLED',
                    blockType: BlockType.COMMAND,
                    text: 'Bit-Z set LED [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'ledStateMenu',
                            defaultValue: 'on'
                        }
                    }
                },

                sectionLabel('Buttons'),
                {
                    opcode: 'whenButtonPressed',
                    blockType: BlockType.HAT,
                    text: 'when button [BUTTON] pressed',
                    isEdgeActivated: true,
                    arguments: {
                        BUTTON: {
                            type: ArgumentType.STRING,
                            menu: 'buttonMenu',
                            defaultValue: 'A'
                        }
                    }
                },
                {
                    opcode: 'whenButtonLongPressed',
                    blockType: BlockType.HAT,
                    text: 'when button [BUTTON] long pressed',
                    isEdgeActivated: true,
                    arguments: {
                        BUTTON: {
                            type: ArgumentType.STRING,
                            menu: 'buttonMenu',
                            defaultValue: 'A'
                        }
                    }
                },
                {
                    opcode: 'isButtonPressed',
                    blockType: BlockType.BOOLEAN,
                    text: 'is button [BUTTON] pressed?',
                    arguments: {
                        BUTTON: {
                            type: ArgumentType.STRING,
                            menu: 'buttonMenu',
                            defaultValue: 'A'
                        }
                    }
                },
                {
                    opcode: 'buttonState',
                    blockType: BlockType.REPORTER,
                    text: 'button [BUTTON] state',
                    arguments: {
                        BUTTON: {
                            type: ArgumentType.STRING,
                            menu: 'buttonMenu',
                            defaultValue: 'A'
                        }
                    }
                },

                sectionLabel('Tilt & Motion'),
                {
                    opcode: 'whenMicrobitGesture',
                    blockType: BlockType.HAT,
                    text: 'when micro:bit [GESTURE]',
                    isEdgeActivated: true,
                    arguments: {
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'shaken'
                        }
                    }
                },
                {
                    opcode: 'isMicrobitGesture',
                    blockType: BlockType.BOOLEAN,
                    text: 'is micro:bit [GESTURE]?',
                    arguments: {
                        GESTURE: {
                            type: ArgumentType.STRING,
                            menu: 'gestureMenu',
                            defaultValue: 'shaken'
                        }
                    }
                },
                {
                    opcode: 'whenMicrobitTilted',
                    blockType: BlockType.HAT,
                    text: 'when micro:bit tilted [TILT]',
                    isEdgeActivated: true,
                    arguments: {
                        TILT: {
                            type: ArgumentType.STRING,
                            menu: 'tiltMenu',
                            defaultValue: 'left'
                        }
                    }
                },
                {
                    opcode: 'isMicrobitTilted',
                    blockType: BlockType.BOOLEAN,
                    text: 'is micro:bit tilted [TILT]?',
                    arguments: {
                        TILT: {
                            type: ArgumentType.STRING,
                            menu: 'tiltMenu',
                            defaultValue: 'left'
                        }
                    }
                },
                {
                    opcode: 'tiltAngle',
                    blockType: BlockType.REPORTER,
                    text: 'tilt angle [TYPE]',
                    arguments: {
                        TYPE: {
                            type: ArgumentType.STRING,
                            menu: 'tiltAngleMenu',
                            defaultValue: 'left-right'
                        }
                    }
                },
                {
                    opcode: 'accelerationAxis',
                    blockType: BlockType.REPORTER,
                    text: 'acceleration [AXIS]',
                    arguments: {
                        AXIS: {
                            type: ArgumentType.STRING,
                            menu: 'axisMenu',
                            defaultValue: 'x'
                        }
                    }
                },
                {
                    opcode: 'accelerationStrength',
                    blockType: BlockType.REPORTER,
                    text: 'acceleration strength'
                },

                sectionLabel('Sensors'),
                {
                    opcode: 'temperature',
                    blockType: BlockType.REPORTER,
                    text: 'temperature'
                },
                {
                    opcode: 'lightLevel',
                    blockType: BlockType.REPORTER,
                    text: 'light level'
                },
                {
                    opcode: 'compassHeading',
                    blockType: BlockType.REPORTER,
                    text: 'compass heading'
                },
                {
                    opcode: 'compassDirection',
                    blockType: BlockType.REPORTER,
                    text: 'compass direction'
                },
                {
                    opcode: 'isTemperatureCompare',
                    blockType: BlockType.BOOLEAN,
                    text: 'is temperature [OP] [VALUE]',
                    arguments: {
                        OP: {
                            type: ArgumentType.STRING,
                            menu: 'compareMenu',
                            defaultValue: '>'
                        },
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 30
                        }
                    }
                },
                {
                    opcode: 'isLightCompare',
                    blockType: BlockType.BOOLEAN,
                    text: 'is light level [OP] [VALUE]',
                    arguments: {
                        OP: {
                            type: ArgumentType.STRING,
                            menu: 'compareMenu',
                            defaultValue: '>'
                        },
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 100
                        }
                    }
                },

                sectionLabel('LED Display'),
                {
                    opcode: 'showText',
                    blockType: BlockType.COMMAND,
                    text: 'show text [TEXT]',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Hello'
                        }
                    }
                },
                {
                    opcode: 'showNumber',
                    blockType: BlockType.COMMAND,
                    text: 'show number [NUMBER]',
                    arguments: {
                        NUMBER: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 123
                        }
                    }
                },
                {
                    opcode: 'showIcon',
                    blockType: BlockType.COMMAND,
                    text: 'show icon [ICON]',
                    arguments: {
                        ICON: {
                            type: ArgumentType.STRING,
                            menu: 'iconMenu',
                            defaultValue: 'heart'
                        }
                    }
                },
                {
                    opcode: 'showArrow',
                    blockType: BlockType.COMMAND,
                    text: 'show arrow [ARROW]',
                    arguments: {
                        ARROW: {
                            type: ArgumentType.STRING,
                            menu: 'arrowMenu',
                            defaultValue: 'north'
                        }
                    }
                },
                {
                    opcode: 'showCustomPattern',
                    blockType: BlockType.COMMAND,
                    text: 'show custom pattern [PATTERN]',
                    arguments: {
                        PATTERN: {
                            type: ArgumentType.STRING,
                            defaultValue: '00100:01110:11111:01110:00100'
                        }
                    }
                },
                {
                    opcode: 'clearDisplay',
                    blockType: BlockType.COMMAND,
                    text: 'clear display'
                },
                {
                    opcode: 'setDisplayBrightness',
                    blockType: BlockType.COMMAND,
                    text: 'set display brightness to [VALUE]',
                    arguments: {
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 100
                        }
                    }
                },

                sectionLabel('Touch Pins'),
                {
                    opcode: 'whenPinTouched',
                    blockType: BlockType.HAT,
                    text: 'when pin [PIN] touched',
                    isEdgeActivated: true,
                    arguments: {
                        PIN: {
                            type: ArgumentType.STRING,
                            menu: 'touchPinMenu',
                            defaultValue: 'P0'
                        }
                    }
                },
                {
                    opcode: 'isPinTouched',
                    blockType: BlockType.BOOLEAN,
                    text: 'is pin [PIN] touched?',
                    arguments: {
                        PIN: {
                            type: ArgumentType.STRING,
                            menu: 'touchPinMenu',
                            defaultValue: 'P0'
                        }
                    }
                },
                {
                    opcode: 'pinTouchValue',
                    blockType: BlockType.REPORTER,
                    text: 'pin [PIN] touch value',
                    arguments: {
                        PIN: {
                            type: ArgumentType.STRING,
                            menu: 'touchPinMenu',
                            defaultValue: 'P0'
                        }
                    }
                }
            ],
            menus: {
                roboMoveMenu: {
                    acceptReporters: true,
                    items: ['forward', 'backward', 'left', 'right']
                },
                roboActionMenu: {
                    acceptReporters: true,
                    items: ['stand', 'sit', 'walk', 'dance', 'handshake', 'pee', 'stop']
                },
                roboServoMenu: {
                    acceptReporters: true,
                    items: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8']
                },
                roboModeMenu: {
                    acceptReporters: true,
                    items: ['robot dog', 'robotic arm', 'Otto robot', 'custom servo robot']
                },
                bitZMoveMenu: {
                    acceptReporters: true,
                    items: ['forward', 'backward']
                },
                bitZTurnMenu: {
                    acceptReporters: true,
                    items: ['left', 'right']
                },
                bitZServoPinMenu: {
                    acceptReporters: true,
                    items: ['P0', 'P1', 'P2']
                },
                ledStateMenu: {
                    acceptReporters: true,
                    items: ['on', 'off', 'toggle']
                },
                buttonMenu: {
                    acceptReporters: true,
                    items: ['A', 'B', 'A+B']
                },
                gestureMenu: {
                    acceptReporters: true,
                    items: ['moved', 'shaken', 'jumped', 'free fall']
                },
                tiltMenu: {
                    acceptReporters: true,
                    items: [
                        'left',
                        'right',
                        'front',
                        'back',
                        'logo up',
                        'logo down',
                        'screen up',
                        'screen down'
                    ]
                },
                tiltAngleMenu: {
                    acceptReporters: true,
                    items: ['left-right', 'front-back']
                },
                axisMenu: {
                    acceptReporters: true,
                    items: ['x', 'y', 'z']
                },
                compareMenu: {
                    acceptReporters: true,
                    items: ['>', '<', '=']
                },
                iconMenu: {
                    acceptReporters: true,
                    items: [
                        'heart',
                        'small heart',
                        'happy',
                        'sad',
                        'yes',
                        'no',
                        'diamond',
                        'square',
                        'triangle',
                        'target',
                        'ghost',
                        'music note'
                    ]
                },
                arrowMenu: {
                    acceptReporters: true,
                    items: [
                        'north',
                        'south',
                        'east',
                        'west',
                        'north-east',
                        'north-west',
                        'south-east',
                        'south-west'
                    ]
                },
                touchPinMenu: {
                    acceptReporters: true,
                    items: ['P0', 'P1', 'P2', 'logo']
                }
            }
        };
    }

    /* ---------------- Connection ---------------- */

    async connectMicrobit () {
    if (!navigator.bluetooth) {
        this.status = 'error: Web Bluetooth not supported';
        console.warn('MadBot micro:bit Link: Web Bluetooth is not supported in this browser.');
        return;
    }

    let step = 'starting';

    try {
        this.connected = false;
        this.rxCharacteristic = null;
        this.txCharacteristic = null;

        step = 'requesting device';
        this.status = step;

        this.device = await navigator.bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: [UART_SERVICE_UUID]
        });

        this.deviceName = this.device.name || 'unknown Bluetooth device';
        this.status = `selected: ${this.deviceName}`;

        console.log('Selected Bluetooth device:', this.deviceName);

        this.device.removeEventListener('gattserverdisconnected', this._onDisconnectedBound);
        this.device.addEventListener('gattserverdisconnected', this._onDisconnectedBound);

        step = 'connecting gatt';
        this.status = step;
        this.server = await this.device.gatt.connect();

        step = 'finding uart service';
        this.status = step;
        this.service = await this.server.getPrimaryService(UART_SERVICE_UUID);

        step = 'reading uart characteristics';
        this.status = step;

        const characteristics = await this.service.getCharacteristics();

        characteristics.forEach(characteristic => {
            const p = characteristic.properties;
            console.log(
                'MadBot micro:bit characteristic:',
                characteristic.uuid,
                {
                    read: p.read,
                    write: p.write,
                    writeWithoutResponse: p.writeWithoutResponse,
                    notify: p.notify,
                    indicate: p.indicate
                }
            );
        });

        /*
         * Choose the characteristic that can accept browser → micro:bit data.
         */
        this.rxCharacteristic = characteristics.find(characteristic =>
            characteristic.properties.writeWithoutResponse ||
            characteristic.properties.write
        );

        /*
         * Choose the characteristic that can send micro:bit → browser data.
         */
        this.txCharacteristic = characteristics.find(characteristic =>
            characteristic.properties.notify ||
            characteristic.properties.indicate
        );

        if (!this.rxCharacteristic) {
            throw new Error('No writable UART characteristic found');
        }

        this.connected = true;
        this.status = 'connected';

        console.log('MadBot micro:bit writable characteristic:', this.rxCharacteristic.uuid);

        if (this.txCharacteristic) {
            console.log('MadBot micro:bit receive characteristic:', this.txCharacteristic.uuid);

            try {
                await this.txCharacteristic.startNotifications();

                this.txCharacteristic.removeEventListener(
                    'characteristicvaluechanged',
                    this._onUartNotificationBound
                );

                this.txCharacteristic.addEventListener(
                    'characteristicvaluechanged',
                    this._onUartNotificationBound
                );

                this.status = 'connected';
            } catch (notifyError) {
                const notifyMessage = notifyError && notifyError.message ?
                    notifyError.message :
                    String(notifyError);

                this.status = `connected, receive not available: ${notifyMessage}`;
                console.warn('MadBot micro:bit Link notification error:', notifyError);
            }
        } else {
            this.status = 'connected, receive characteristic not found';
        }

        console.log(`MadBot micro:bit Link connected to ${this.deviceName}`);
    } catch (error) {
        this.connected = false;

        const message = error && error.message ? error.message : String(error);
        this.status = `error at ${step}: ${message}`;

        console.warn('MadBot micro:bit Link connection error:', error);

        try {
            if (this.device && this.device.gatt && this.device.gatt.connected) {
                this.device.gatt.disconnect();
            }
        } catch (disconnectError) {
            console.warn('MadBot micro:bit Link disconnect after error failed:', disconnectError);
        }
    }
}

    disconnectMicrobit () {
        try {
            if (this.txCharacteristic) {
                this.txCharacteristic.removeEventListener('characteristicvaluechanged', this._onUartNotificationBound);
            }

            if (this.device && this.device.gatt && this.device.gatt.connected) {
                this.device.gatt.disconnect();
            }
        } catch (error) {
            console.warn('MadBot micro:bit Link disconnect error:', error);
        }

        this.connected = false;
        this.status = 'not connected';
    }

    handleDisconnected () {
        this.connected = false;
        this.status = 'not connected';
        console.warn('MadBot micro:bit Link disconnected.');
    }

    isMicrobitConnected () {
        return !!(this.connected && this.device && this.device.gatt && this.device.gatt.connected);
    }

    microbitName () {
        return this.deviceName || '';
    }

    connectionStatus () {
        return this.status || 'not connected';
    }

    /* ---------------- UART send / receive ---------------- */

    async sendMessage (args) {
        await this.sendUartText(String(args.MESSAGE || ''));
    }

    async sendCommand (args) {
        await this.sendUartText(String(args.COMMAND || ''));
    }

    async sendValue (args) {
        const key = String(args.KEY || '').trim();
        const value = String(args.VALUE || '').trim();

        if (!key) return;

        await this.sendUartText(`VALUE:${key}:${value}`);
    }

    async sendUartText (text) {
    if (!this.isMicrobitConnected() || !this.rxCharacteristic) {
        console.warn('MadBot micro:bit Link: micro:bit not connected.');
        this.status = 'connected, but send failed: RX characteristic not ready';
        return;
    }

    try {
        const encoder = new TextEncoder();
        const data = encoder.encode(`${text}\n`);

        const chunkSize = 20;

        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);

            if (
                this.rxCharacteristic.properties &&
                this.rxCharacteristic.properties.writeWithoutResponse
            ) {
                await this.rxCharacteristic.writeValueWithoutResponse(chunk);
            } else if (
                this.rxCharacteristic.properties &&
                this.rxCharacteristic.properties.write
            ) {
                await this.rxCharacteristic.writeValue(chunk);
            } else {
                throw new Error('Selected UART characteristic does not support write');
            }

            await this.sleep(20);
        }

        console.log('MadBot micro:bit Link sent:', text);
    } catch (error) {
        const message = error && error.message ? error.message : String(error);
        this.status = `connected, but send error: ${message}`;
        console.warn('MadBot micro:bit Link send error:', error);
    }
}

    handleUartNotification (event) {
        try {
            const value = event.target.value;
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(value);

            this.rxBuffer += text.replace(/\r/g, '\n');
            const lines = this.rxBuffer.split('\n');

            this.rxBuffer = lines.pop();

            lines.forEach(line => {
                const cleanLine = line.trim();
                if (cleanLine) this.processReceivedLine(cleanLine);
            });
        } catch (error) {
            console.warn('MadBot micro:bit Link receive error:', error);
        }
    }

    processReceivedLine (message) {
        this.lastReceivedMessage = message;
        this.messageCounter += 1;
        this.incrementEvent('MESSAGE');

        this.parseMessage(message);
    }

    parseMessage (message) {
        const parts = String(message).trim().split(':');
        if (parts.length < 1) return;

        const type = String(parts[0] || '').toUpperCase();

        if (type === 'VALUE' && parts.length >= 3) {
            const key = String(parts[1]).toLowerCase();
            const value = parts.slice(2).join(':');
            this.receivedValues[key] = value;
            return;
        }

        if (type === 'TEMP') {
            this.sensorValues.temperature = Cast.toNumber(parts[1]);
            this.receivedValues.temperature = this.sensorValues.temperature;
            return;
        }

        if (type === 'LIGHT') {
            this.sensorValues.light = Cast.toNumber(parts[1]);
            this.receivedValues.light = this.sensorValues.light;
            return;
        }

        if (type === 'COMPASS') {
            this.sensorValues.compass = Cast.toNumber(parts[1]);
            this.receivedValues.compass = this.sensorValues.compass;
            return;
        }

        if (type === 'ACC' && parts.length >= 3) {
            const axis = String(parts[1]).toLowerCase();
            this.acceleration[axis] = Cast.toNumber(parts[2]);
            this.receivedValues[`acc_${axis}`] = this.acceleration[axis];
            return;
        }

        if (type === 'BTN' && parts.length >= 3) {
            const button = this.normalizeButton(parts[1]);
            const state = this.normalizeButtonState(parts[2]);

            this.buttonStates[button] = state;

            if (state === 'pressed') this.incrementEvent(`BTN:${button}:PRESSED`);
            if (state === 'long pressed') this.incrementEvent(`BTN:${button}:LONG`);

            return;
        }

        if (type === 'GESTURE' && parts.length >= 2) {
            const gesture = this.normalizeGesture(parts[1]);
            this.currentGesture = gesture;
            this.incrementEvent(`GESTURE:${gesture}`);
            return;
        }

        if (type === 'TILT' && parts.length >= 2) {
            const tilt = this.normalizeTilt(parts[1]);
            this.currentTilt = tilt;
            this.incrementEvent(`TILT:${tilt}`);
            return;
        }

        if (type === 'PIN' && parts.length >= 3) {
            const pin = this.normalizePin(parts[1]);
            const state = String(parts[2] || '').toUpperCase();

            this.pinStates[pin] = state === 'TOUCHED' || state === 'PRESSED';
            this.pinValues[pin] = this.pinStates[pin] ? 1 : 0;

            if (this.pinStates[pin]) this.incrementEvent(`PIN:${pin}:TOUCHED`);

            return;
        }

        // Generic KEY:VALUE support
        if (parts.length >= 2) {
            this.receivedValues[String(parts[0]).toLowerCase()] = parts.slice(1).join(':');
        }
    }

    whenMessageReceived () {
        if (this.messageCounter !== this.lastMessageHatCounter) {
            this.lastMessageHatCounter = this.messageCounter;
            return true;
        }
        return false;
    }

    receivedMessage () {
        return this.lastReceivedMessage || '';
    }

    receivedValue (args) {
        const key = String(args.KEY || '').toLowerCase();
        return this.receivedValues[key] !== undefined ? this.receivedValues[key] : '';
    }

    clearReceivedMessage () {
        this.lastReceivedMessage = '';
    }

    /* ---------------- RoboMorph commands ---------------- */

    async roboMorphMove (args) {
        const direction = String(args.DIRECTION || 'forward').toUpperCase();
        const speed = this.clamp(Math.round(Cast.toNumber(args.SPEED)), 0, 5);
        await this.sendUartText(`RM:MOVE:${direction}:${speed}`);
    }

    async roboMorphStop () {
        await this.sendUartText('RM:STOP');
    }

    async roboMorphAction (args) {
        const action = String(args.ACTION || 'stop').toUpperCase();
        await this.sendUartText(`RM:ACTION:${action}`);
    }

    async roboMorphServo (args) {
        const servo = String(args.SERVO || 'S1').toUpperCase();
        const angle = this.clamp(Math.round(Cast.toNumber(args.ANGLE)), 0, 180);
        await this.sendUartText(`RM:SERVO:${servo}:${angle}`);
    }

    async roboMorphMode (args) {
        const mode = this.mapRoboMode(args.MODE);
        await this.sendUartText(`RM:MODE:${mode}`);
    }

    mapRoboMode (mode) {
        const value = String(mode || '').toLowerCase();

        if (value === 'robot dog') return 'DOG';
        if (value === 'robotic arm') return 'ARM';
        if (value === 'otto robot') return 'OTTO';
        if (value === 'custom servo robot') return 'CUSTOM';

        return 'CUSTOM';
    }

    /* ---------------- Bit-Z commands ---------------- */

    async bitZCarMove (args) {
        const direction = String(args.DIRECTION || 'forward').toUpperCase();
        const speed = this.clamp(Math.round(Cast.toNumber(args.SPEED)), 0, 100);
        await this.sendUartText(`BZ:CAR:${direction}:${speed}`);
    }

    async bitZCarTurn (args) {
        const direction = String(args.DIRECTION || 'left').toUpperCase();
        const speed = this.clamp(Math.round(Cast.toNumber(args.SPEED)), 0, 100);
        await this.sendUartText(`BZ:CAR:${direction}:${speed}`);
    }

    async bitZCarStop () {
        await this.sendUartText('BZ:CAR:STOP');
    }

    async bitZServo (args) {
        const pin = String(args.PIN || 'P0').toUpperCase();
        const angle = this.clamp(Math.round(Cast.toNumber(args.ANGLE)), 0, 180);
        await this.sendUartText(`BZ:SERVO:${pin}:${angle}`);
    }

    async bitZShowText (args) {
        await this.sendUartText(`BZ:TEXT:${String(args.TEXT || '')}`);
    }

    async bitZSetLED (args) {
        const state = String(args.STATE || 'on').toUpperCase();
        await this.sendUartText(`BZ:LED:${state}`);
    }

    /* ---------------- Buttons ---------------- */

    whenButtonPressed (args) {
        const button = this.normalizeButton(args.BUTTON);
        return this.consumeEvent(`BTN:${button}:PRESSED`);
    }

    whenButtonLongPressed (args) {
        const button = this.normalizeButton(args.BUTTON);
        return this.consumeEvent(`BTN:${button}:LONG`);
    }

    isButtonPressed (args) {
        const button = this.normalizeButton(args.BUTTON);
        return this.buttonStates[button] === 'pressed' || this.buttonStates[button] === 'long pressed';
    }

    buttonState (args) {
        const button = this.normalizeButton(args.BUTTON);
        return this.buttonStates[button] || 'not pressed';
    }

    /* ---------------- Tilt & Motion ---------------- */

    whenMicrobitGesture (args) {
        const gesture = this.normalizeGesture(args.GESTURE);
        return this.consumeEvent(`GESTURE:${gesture}`);
    }

    isMicrobitGesture (args) {
        const gesture = this.normalizeGesture(args.GESTURE);
        return this.currentGesture === gesture;
    }

    whenMicrobitTilted (args) {
        const tilt = this.normalizeTilt(args.TILT);
        return this.consumeEvent(`TILT:${tilt}`);
    }

    isMicrobitTilted (args) {
        const tilt = this.normalizeTilt(args.TILT);
        return this.currentTilt === tilt;
    }

    tiltAngle (args) {
        const type = String(args.TYPE || '').toLowerCase();

        if (type === 'left-right') return this.acceleration.x || 0;
        if (type === 'front-back') return this.acceleration.y || 0;

        return 0;
    }

    accelerationAxis (args) {
        const axis = String(args.AXIS || 'x').toLowerCase();
        return this.acceleration[axis] || 0;
    }

    accelerationStrength () {
        const x = this.acceleration.x || 0;
        const y = this.acceleration.y || 0;
        const z = this.acceleration.z || 0;

        return Math.round(Math.sqrt((x * x) + (y * y) + (z * z)));
    }

    /* ---------------- Sensors ---------------- */

    temperature () {
        return this.sensorValues.temperature || 0;
    }

    lightLevel () {
        return this.sensorValues.light || 0;
    }

    compassHeading () {
        return this.sensorValues.compass || 0;
    }

    compassDirection () {
        return this.headingToDirection(this.sensorValues.compass || 0);
    }

    isTemperatureCompare (args) {
        return this.compare(this.temperature(), args.OP, Cast.toNumber(args.VALUE));
    }

    isLightCompare (args) {
        return this.compare(this.lightLevel(), args.OP, Cast.toNumber(args.VALUE));
    }

    /* ---------------- LED display ---------------- */

    async showText (args) {
        await this.sendUartText(`LED:TEXT:${String(args.TEXT || '')}`);
    }

    async showNumber (args) {
        await this.sendUartText(`LED:NUMBER:${Cast.toNumber(args.NUMBER)}`);
    }

    async showIcon (args) {
        const icon = this.commandText(args.ICON);
        await this.sendUartText(`LED:ICON:${icon}`);
    }

    async showArrow (args) {
        const arrow = this.commandText(args.ARROW);
        await this.sendUartText(`LED:ARROW:${arrow}`);
    }

    async showCustomPattern (args) {
        await this.sendUartText(`LED:PATTERN:${String(args.PATTERN || '')}`);
    }

    async clearDisplay () {
        await this.sendUartText('LED:CLEAR');
    }

    async setDisplayBrightness (args) {
        const brightness = this.clamp(Math.round(Cast.toNumber(args.VALUE)), 0, 255);
        await this.sendUartText(`LED:BRIGHTNESS:${brightness}`);
    }

    /* ---------------- Touch pins ---------------- */

    whenPinTouched (args) {
        const pin = this.normalizePin(args.PIN);
        return this.consumeEvent(`PIN:${pin}:TOUCHED`);
    }

    isPinTouched (args) {
        const pin = this.normalizePin(args.PIN);
        return !!this.pinStates[pin];
    }

    pinTouchValue (args) {
        const pin = this.normalizePin(args.PIN);
        return this.pinValues[pin] || 0;
    }

    /* ---------------- Helpers ---------------- */

    incrementEvent (key) {
        this.eventCounters[key] = (this.eventCounters[key] || 0) + 1;
    }

    consumeEvent (key) {
        const current = this.eventCounters[key] || 0;
        const last = this.lastHatCounters[key] || 0;

        if (current !== last) {
            this.lastHatCounters[key] = current;
            return true;
        }

        return false;
    }

    normalizeButton (button) {
        const value = String(button || '').toUpperCase().replace(/\s/g, '');

        if (value === 'AB' || value === 'A+B') return 'A+B';
        if (value === 'B') return 'B';

        return 'A';
    }

    normalizeButtonState (state) {
        const value = String(state || '').toUpperCase();

        if (value === 'LONG' || value === 'LONGPRESSED' || value === 'LONG_PRESSED') {
            return 'long pressed';
        }

        if (value === 'PRESSED' || value === 'DOWN') return 'pressed';

        return 'not pressed';
    }

    normalizeGesture (gesture) {
        const value = String(gesture || '').toLowerCase().replace(/_/g, ' ');

        if (value === 'shaken' || value === 'shake') return 'shaken';
        if (value === 'jumped' || value === 'jump') return 'jumped';
        if (value === 'freefall' || value === 'free fall') return 'free fall';
        if (value === 'moved' || value === 'move') return 'moved';

        return value;
    }

    normalizeTilt (tilt) {
        return String(tilt || '').toLowerCase().replace(/_/g, ' ');
    }

    normalizePin (pin) {
        const value = String(pin || '').toUpperCase();

        if (value === 'LOGO') return 'LOGO';
        if (value === 'P1') return 'P1';
        if (value === 'P2') return 'P2';

        return 'P0';
    }

    commandText (text) {
        return String(text || '').toUpperCase().replace(/[\s-]+/g, '_');
    }

    headingToDirection (heading) {
        const h = ((Math.round(Cast.toNumber(heading)) % 360) + 360) % 360;

        if (h >= 337.5 || h < 22.5) return 'north';
        if (h >= 22.5 && h < 67.5) return 'north-east';
        if (h >= 67.5 && h < 112.5) return 'east';
        if (h >= 112.5 && h < 157.5) return 'south-east';
        if (h >= 157.5 && h < 202.5) return 'south';
        if (h >= 202.5 && h < 247.5) return 'south-west';
        if (h >= 247.5 && h < 292.5) return 'west';

        return 'north-west';
    }

    compare (left, op, right) {
        const operator = String(op || '>');

        if (operator === '>') return left > right;
        if (operator === '<') return left < right;
        if (operator === '=') return left === right;

        return false;
    }

    clamp (value, min, max) {
        return Math.max(min, Math.min(max, value));
    }

    sleep (ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

module.exports = Scratch3MadBotMicrobitLink;